import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import PopperJs from 'popper.js';
import React, { useRef, useImperativeHandle, useState, useEffect, useCallback, useMemo } from 'react';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import useIsomorphicEffect from '../../../hooks/use-isomorphic-effect.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { callAll } from '../../../utils/function.esm.js';
import setRef from '../../../utils/set-ref.esm.js';
import { BoxPrivate } from '../../_primitives/box/box.esm.js';
import Portal from '../portal/portal.esm.js';

var _excluded = ["anchorEl", "children", "container", "disablePortal", "keepMounted", "modifiers", "open", "placement", "popperOptions", "popperRef", "style", "transition", "shouldHoistTheme", "__css"];
function flipPlacement(placement, theme) {
  var direction = theme && theme.direction || 'ltr';
  if (direction === 'ltr') {
    return placement;
  }
  switch (placement) {
    case 'bottom-end':
      return 'bottom-start';
    case 'bottom-start':
      return 'bottom-end';
    case 'top-end':
      return 'top-start';
    case 'top-start':
      return 'top-end';
    default:
      return placement;
  }
}
function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
var defaultPopperOptions = {};
var Popper = /*#__PURE__*/React.forwardRef(function Popper(props, ref) {
  var anchorEl = props.anchorEl,
    children = props.children,
    container = props.container,
    _props$disablePortal = props.disablePortal,
    disablePortal = _props$disablePortal === void 0 ? false : _props$disablePortal,
    _props$keepMounted = props.keepMounted,
    keepMounted = _props$keepMounted === void 0 ? false : _props$keepMounted,
    modifiers = props.modifiers,
    open = props.open,
    _props$placement = props.placement,
    initialPlacement = _props$placement === void 0 ? 'bottom' : _props$placement,
    _props$popperOptions = props.popperOptions,
    popperOptions = _props$popperOptions === void 0 ? defaultPopperOptions : _props$popperOptions,
    popperRefProp = props.popperRef,
    style = props.style,
    _props$transition = props.transition,
    transition = _props$transition === void 0 ? false : _props$transition,
    _props$shouldHoistThe = props.shouldHoistTheme,
    shouldHoistTheme = _props$shouldHoistThe === void 0 ? true : _props$shouldHoistThe,
    __css = props.__css,
    other = _objectWithoutProperties(props, _excluded);
  var tooltipRef = useRef(null);
  var ownRef = useForkRef(tooltipRef, ref);
  var popperRef = useRef(null);
  var handlePopperRef = useForkRef(popperRef, popperRefProp);
  var handlePopperRefRef = useRef(handlePopperRef);
  useIsomorphicEffect(function () {
    handlePopperRefRef.current = handlePopperRef;
  }, [handlePopperRef]);
  useImperativeHandle(popperRefProp, function () {
    return popperRef.current;
  }, []);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    exited = _useState2[0],
    setExited = _useState2[1];
  var theme = useSafeThemeUI(props);
  var rtlPlacement = flipPlacement(initialPlacement, theme);
  /**
   * placement initialized from prop but can change during lifetime if modifiers.flip.
   * modifiers.flip is essentially a flip for controlled/uncontrolled behavior
   */
  var _useState3 = useState(rtlPlacement),
    _useState4 = _slicedToArray(_useState3, 2),
    placement = _useState4[0],
    setPlacement = _useState4[1];
  useEffect(function () {
    if (popperRef.current) {
      popperRef.current.update();
    }
  });
  var handleOpen = useCallback(function () {
    if (!tooltipRef.current || !anchorEl || !open) {
      return;
    }
    if (popperRef.current) {
      popperRef.current.destroy();
      handlePopperRefRef && handlePopperRefRef.current && typeof handlePopperRefRef.current === 'function' && handlePopperRefRef.current(null);
    }
    var handlePopperUpdate = function handlePopperUpdate(data) {
      setPlacement(data.placement);
    };
    var resolvedAnchorEl = getAnchorEl(anchorEl);
    if (process.env.NODE_ENV !== 'production') {
      if (resolvedAnchorEl && resolvedAnchorEl.nodeType === 1) {
        var box = resolvedAnchorEl.getBoundingClientRect();
        if (process.env.NODE_ENV !== 'test' && box.top === 0 && box.left === 0 && box.right === 0 && box.bottom === 0) {
          // eslint-disable-next-line no-console
          console.warn(['Tui: the `anchorEl` prop provided to the component is invalid.', 'The anchor element should be part of the document layout.', "Make sure the element is present in the document or that it's not display none."].join('\n'));
        }
      }
    }
    var popper = new PopperJs(getAnchorEl(anchorEl), tooltipRef.current, _objectSpread2(_objectSpread2({
      placement: rtlPlacement
    }, popperOptions), {}, {
      modifiers: _objectSpread2(_objectSpread2(_objectSpread2({}, disablePortal ? {} : {
        // It's using scrollParent by default, we can use the viewport when using a portal.
        preventOverflow: {
          boundariesElement: 'window'
        }
      }), modifiers), popperOptions.modifiers),
      // We could have been using a custom modifier like react-popper is doing.
      // But it seems this is the best public API for this use case.
      onCreate: callAll(handlePopperUpdate, popperOptions.onCreate),
      onUpdate: callAll(handlePopperUpdate, popperOptions.onUpdate)
    }));
    handlePopperRefRef && handlePopperRefRef.current && typeof handlePopperRefRef.current === 'function' && handlePopperRefRef.current(popper);
  }, [anchorEl, disablePortal, modifiers, open, rtlPlacement, popperOptions, tooltipRef]);
  var handleRef = useCallback(function (node) {
    setRef(ownRef, node);
    handleOpen();
  }, [ownRef, handleOpen]);
  var handleEnter = function handleEnter() {
    setExited(false);
  };
  var handleClose = function handleClose() {
    if (!popperRef.current) {
      return;
    }
    popperRef.current.destroy();
    handlePopperRefRef && handlePopperRefRef.current && typeof handlePopperRefRef.current === 'function' && handlePopperRefRef.current(null);
  };
  var handleExited = function handleExited() {
    setExited(true);
    handleClose();
  };
  useEffect(function () {
    // Let's update the popper position.
    handleOpen();
  }, [handleOpen]);
  useEffect(function () {
    return function () {
      handleClose();
    };
  }, []);
  useEffect(function () {
    if (!open && !transition) {
      // Otherwise handleExited will call this.
      handleClose();
    }
  }, [open, transition]);
  var childProps = {
    placement: placement
  };
  if (transition) {
    childProps.TransitionProps = {
      "in": open,
      onEnter: handleEnter,
      onExited: handleExited
    };
  }
  var styles = useMemo(function () {
    return _objectSpread2(_objectSpread2({
      // Prevents scroll issue, waiting for Popper.js to add this style once initiated.
      position: 'fixed',
      // Fix Popper.js display issue
      top: 0,
      left: 0
    }, __css), style);
  }, [__css, style]);
  return !keepMounted && !open && (!transition || exited) ? null : /*#__PURE__*/React.createElement(Portal, {
    disablePortal: disablePortal,
    container: container,
    shouldHoistTheme: shouldHoistTheme && !disablePortal
  }, /*#__PURE__*/React.createElement(BoxPrivate, _extends({
    ref: handleRef,
    role: "tooltip"
  }, other, {
    __css: styles
  }), typeof children === 'function' ? children(childProps) : children));
});

export default Popper;
//# sourceMappingURL=popper.esm.js.map
